import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import {
  IconCheck,
  IconCopy,
  IconExternalLink,
  IconKey,
  IconRefresh,
  IconSend,
  IconTrash,
} from '@tabler/icons-react';
import { Chip } from '../chip';
import { formatDueDate as formatSydneyDateTime } from '../../utils/todoDisplay';
import { normaliseAuMobile } from '../../utils/auPhone';
import {
  isLiveCredentialRequest,
  issueCredentialRequest,
  relinkCredentialRequest,
  revokeCredentialRequest,
  type CredentialDeliveryChannel,
  type CredentialRequestDto,
} from '../../services/credentialRequestsApi';
import './CredentialRequestDialog.css';

/** Matches MAX_LABEL_LENGTH in sm4rt-api. Feedback only — the server re-validates. */
const MAX_LABEL_LENGTH = 200;

/** §9's cap, per request. A relink shares the counter and cannot reset it. */
const SUBMISSION_CAP = 10;

type Channel = CredentialDeliveryChannel | 'none';

export interface CredentialRequestDialogProps {
  open: boolean;
  onClose: () => void;
  /** Every request on this ticket, newest first. */
  requests: CredentialRequestDto[];
  /** The one that can still take submissions, if any. */
  liveRequest: CredentialRequestDto | null;
  isLoading: boolean;
  /** Load error from the list fetch. */
  loadError: string | null;
  /** Re-read the list after a write. */
  onRefresh: () => void;
  /** RepairShopr ticket id the request binds to. */
  rsTicketId: string;
  /** Suggested request name — the customer reads it, so it is editable. */
  defaultLabel: string;
  customerEmail: string;
  customerMobile: string;
  /** Write a line to the job log. Never called with the link or the token. */
  onLog: (text: string) => void;
}

const CHANNEL_LABELS: Record<Channel, string> = {
  email: 'Email',
  sms: 'SMS',
  none: "Don't send",
};

function stateTone(request: CredentialRequestDto) {
  if (request.revoked || request.state === 'revoked') return 'error' as const;
  if (request.state === 'expired') return 'neutral' as const;
  if (request.state === 'submitted') return 'success' as const;
  return 'info' as const;
}

function stateLabel(request: CredentialRequestDto) {
  if (request.revoked || request.state === 'revoked') return 'Revoked';
  if (request.state === 'expired') return 'Expired';
  if (request.state === 'submitted') return 'Submitted';
  return 'Awaiting customer';
}

/**
 * Raise and manage a secure credential request for a ticket.
 *
 * Two things this deliberately never does:
 *
 * - **It does not show a credential.** A submission renders its sequence, its
 *   outcome, when it landed and a 1Password deep link. The technician opens the
 *   item under their own 1Password session, where the viewing authorisation and
 *   the audit trail live; SM4RT holds no 1Password credential at all.
 * - **It does not keep the link.** The URL carries a signed capability that
 *   sm4rt-api never stores, so it lives in this component's state and nowhere
 *   else — no `sessionStorage`, no job-log entry, no error message. Once the
 *   dialog closes it is gone, and "Resend" mints another one for the same
 *   request rather than recovering it.
 */
export const CredentialRequestDialog: React.FC<CredentialRequestDialogProps> = ({
  open,
  onClose,
  requests,
  liveRequest,
  isLoading,
  loadError,
  onRefresh,
  rsTicketId,
  defaultLabel,
  customerEmail,
  customerMobile,
  onLog,
}) => {
  const [label, setLabel] = useState(defaultLabel);
  const [channel, setChannel] = useState<Channel>(customerEmail ? 'email' : 'none');
  const [destination, setDestination] = useState(customerEmail);
  const [link, setLink] = useState('');
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [confirmRevoke, setConfirmRevoke] = useState(false);

  // Reset per opening: a link from a previous session of the dialog must not
  // reappear, and the form should reflect the current ticket.
  useEffect(() => {
    if (!open) return;
    setLabel(defaultLabel);
    setChannel(customerEmail ? 'email' : 'none');
    setDestination(customerEmail);
    setLink('');
    setCopied(false);
    setActionError(null);
    setConfirmRevoke(false);
  }, [open, defaultLabel, customerEmail]);

  const pickChannel = useCallback(
    (next: Channel) => {
      setChannel(next);
      if (next === 'email') setDestination(customerEmail);
      if (next === 'sms') setDestination(normaliseAuMobile(customerMobile) ?? customerMobile);
    },
    [customerEmail, customerMobile],
  );

  const copyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard denied — the field is selectable, so fall through silently */
    }
  }, [link]);

  const deliveryFields = () =>
    channel === 'none' ? {} : { deliverTo: destination.trim(), deliverVia: channel };

  /** Describe what happened to the send, for the job log and the user. */
  const deliveryNote = (result: { deliveredTo: string; deliveredVia: string }) =>
    result.deliveredTo ? `${result.deliveredTo} (${result.deliveredVia})` : '';

  const handleCreate = async () => {
    const trimmed = label.trim();
    if (!trimmed) {
      setActionError('A request name is required — the customer sees it.');
      return;
    }
    if (channel !== 'none' && !destination.trim()) {
      setActionError(`Enter the ${CHANNEL_LABELS[channel].toLowerCase()} address to send to.`);
      return;
    }

    setBusy(true);
    setActionError(null);
    try {
      const result = await issueCredentialRequest({
        label: trimmed,
        ticketId: rsTicketId,
        ...deliveryFields(),
      });
      setLink(result.link);
      onLog(
        `Credential request created: "${trimmed}" — expires ${formatSydneyDateTime(result.expiresAt)}.`,
      );
      if (result.deliveryError) {
        // The request is real and the link usable; only the send failed. Say
        // that rather than letting anyone believe the customer was contacted.
        setActionError(`The request was created, but the link was not sent: ${result.deliveryError}`);
      } else if (result.deliveredTo) {
        onLog(`Secure credential link sent to ${deliveryNote(result)}.`);
      }
      onRefresh();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to create the credential request');
    } finally {
      setBusy(false);
    }
  };

  const handleRelink = async (send: boolean) => {
    if (!liveRequest) return;
    if (send && !destination.trim()) {
      setActionError(`Enter the ${CHANNEL_LABELS[channel].toLowerCase()} address to send to.`);
      return;
    }

    setBusy(true);
    setActionError(null);
    try {
      const result = await relinkCredentialRequest(
        liveRequest.requestId,
        send ? deliveryFields() : {},
      );
      setLink(result.link);
      if (result.deliveryError) {
        setActionError(`A new link was created, but it was not sent: ${result.deliveryError}`);
        onLog('Secure credential link regenerated.');
      } else if (result.deliveredTo) {
        onLog(`Secure credential link sent to ${deliveryNote(result)}.`);
      } else {
        onLog('Secure credential link regenerated.');
      }
      onRefresh();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to regenerate the link');
    } finally {
      setBusy(false);
    }
  };

  const handleRevoke = async () => {
    if (!liveRequest) return;
    setBusy(true);
    setActionError(null);
    try {
      await revokeCredentialRequest(liveRequest.requestId);
      setLink('');
      onLog('Credential request revoked — every link for it is now dead.');
      onRefresh();
      setConfirmRevoke(false);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to revoke the credential request');
    } finally {
      setBusy(false);
    }
  };

  const history = requests.filter((r) => !isLiveCredentialRequest(r));

  const linkPanel = link && (
    <div className="cred-dlg__section">
      <div className="cred-dlg__section-hdr">
        <span className="cred-dlg__section-title">The link to send</span>
      </div>
      <div className="cred-dlg__section-body">
        <div className="cred-dlg__linkrow">
          <input className="cred-dlg__linkfield" value={link} readOnly />
          <button
            type="button"
            className="cred-dlg__iconbtn"
            onClick={copyLink}
            title={copied ? 'Copied' : 'Copy link'}
          >
            {copied ? <IconCheck size={16} stroke={1.5} /> : <IconCopy size={16} stroke={1.5} />}
          </button>
        </div>
        <p className="cred-dlg__hint">
          This URL is shown once and cannot be recovered — nothing stores it. Copy it now if
          you are sending it yourself; otherwise use Resend, which creates a new link for the
          same request.
        </p>
      </div>
    </div>
  );

  const deliveryControls = (
    <>
      <div className="cred-dlg__field">
        <span className="cred-dlg__label">Send to the customer</span>
        <div className="cred-dlg__radios">
          {(['email', 'sms', 'none'] as Channel[]).map((option) => (
            <label
              key={option}
              className={`cred-dlg__radio${channel === option ? ' cred-dlg__radio--selected' : ''}`}
            >
              <input
                type="radio"
                name="cred-channel"
                checked={channel === option}
                onChange={() => pickChannel(option)}
                disabled={busy}
              />
              {CHANNEL_LABELS[option]}
            </label>
          ))}
        </div>
      </div>
      {channel !== 'none' && (
        <div className="cred-dlg__field">
          <span className="cred-dlg__label">
            {channel === 'email' ? 'Email address' : 'Mobile number'}
          </span>
          <input
            className="cred-dlg__input"
            value={destination}
            onChange={(e) => setDestination(e.target.value)}
            disabled={busy}
            placeholder={channel === 'email' ? 'customer@example.com' : '+61412345678'}
          />
          <span className="cred-dlg__hint">
            Only the masked form is recorded against the request.
          </span>
        </div>
      )}
    </>
  );

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <IconKey size={18} stroke={1.5} />
        {liveRequest ? 'Credential request' : 'New credential request'}
      </DialogTitle>
      <DialogContent>
        <div className="cred-dlg">
          {loadError && <Alert severity="error">{loadError}</Alert>}
          {actionError && <Alert severity="warning">{actionError}</Alert>}

          {linkPanel}

          {isLoading && !liveRequest && (
            <p className="cred-dlg__empty">Loading this ticket's credential requests…</p>
          )}

          {!liveRequest && !isLoading && (
            <div className="cred-dlg__section">
              <div className="cred-dlg__section-hdr">
                <span className="cred-dlg__section-title">Ask the customer for a credential</span>
              </div>
              <div className="cred-dlg__section-body">
                <div className="cred-dlg__field">
                  <span className="cred-dlg__label">Request name</span>
                  <input
                    className="cred-dlg__input"
                    value={label}
                    maxLength={MAX_LABEL_LENGTH}
                    onChange={(e) => setLabel(e.target.value)}
                    disabled={busy}
                  />
                  <span className="cred-dlg__hint">
                    The customer reads this on the submission page, and it becomes the
                    1Password item title. Keep customer names and addresses out of it.
                  </span>
                  <span className="cred-dlg__hint cred-dlg__hint--count">
                    {label.length} / {MAX_LABEL_LENGTH}
                  </span>
                </div>
                {deliveryControls}
              </div>
            </div>
          )}

          {liveRequest && (
            <>
              <div className="cred-dlg__section">
                <div className="cred-dlg__section-hdr">
                  <span className="cred-dlg__section-title">{liveRequest.label}</span>
                  <Chip tone={stateTone(liveRequest)}>{stateLabel(liveRequest)}</Chip>
                </div>
                <div className="cred-dlg__section-body">
                  <div className="cred-dlg__facts">
                    <div>
                      <div className="cred-dlg__fact-label">Raised by</div>
                      <div className="cred-dlg__fact-value">{liveRequest.issuedBy || '—'}</div>
                    </div>
                    <div>
                      <div className="cred-dlg__fact-label">Raised</div>
                      <div className="cred-dlg__fact-value">
                        {formatSydneyDateTime(liveRequest.issuedAt)}
                      </div>
                    </div>
                    <div>
                      <div className="cred-dlg__fact-label">Link expires</div>
                      <div className="cred-dlg__fact-value">
                        {formatSydneyDateTime(liveRequest.expiresAt)}
                      </div>
                    </div>
                    <div>
                      <div className="cred-dlg__fact-label">Last sent to</div>
                      <div
                        className={`cred-dlg__fact-value${
                          liveRequest.deliveredTo ? '' : ' cred-dlg__fact-value--muted'
                        }`}
                      >
                        {liveRequest.deliveredTo
                          ? `${liveRequest.deliveredTo} · ${formatSydneyDateTime(liveRequest.deliveredAt)}`
                          : 'Not sent from SM4RT'}
                      </div>
                    </div>
                  </div>
                  {liveRequest.submissionCount >= SUBMISSION_CAP && (
                    <Alert severity="warning">
                      This request has reached its limit of {SUBMISSION_CAP} submissions. A new
                      link will not raise it — create a new request if the customer needs to
                      send another.
                    </Alert>
                  )}
                </div>
              </div>

              <div className="cred-dlg__section">
                <div className="cred-dlg__section-hdr">
                  <span className="cred-dlg__section-title">
                    Received · {liveRequest.submissions.length}
                  </span>
                </div>
                {liveRequest.submissions.length === 0 ? (
                  <p className="cred-dlg__empty">Nothing submitted yet.</p>
                ) : (
                  liveRequest.submissions.map((submission) => (
                    <div className="cred-dlg__sub" key={submission.id || submission.idemKey}>
                      <span className="cred-dlg__sub-seq">#{submission.submissionSeq}</span>
                      <div className="cred-dlg__sub-main">
                        <Chip tone={submission.outcome === 'SUCCESS' ? 'success' : 'error'}>
                          {submission.outcome === 'SUCCESS' ? 'Stored' : 'Failed'}
                        </Chip>
                        <span className="cred-dlg__sub-time">
                          {formatSydneyDateTime(submission.completedAt)}
                        </span>
                      </div>
                      {submission.deepLink && (
                        <a
                          className="cred-dlg__iconbtn"
                          href={submission.deepLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="Open in 1Password"
                        >
                          <IconExternalLink size={16} stroke={1.5} />
                        </a>
                      )}
                    </div>
                  ))
                )}
              </div>

              <div className="cred-dlg__section">
                <div className="cred-dlg__section-hdr">
                  <span className="cred-dlg__section-title">Send another link</span>
                </div>
                <div className="cred-dlg__section-body">
                  <p className="cred-dlg__hint">
                    The original URL cannot be read back. Resending creates a new link for this
                    same request — the earlier one keeps working until it expires or you revoke.
                  </p>
                  {deliveryControls}
                  <div className="cred-dlg__actions">
                    <button
                      type="button"
                      className="cred-dlg__btn cred-dlg__btn--primary"
                      onClick={() => handleRelink(true)}
                      disabled={busy || channel === 'none'}
                    >
                      <IconSend size={14} stroke={1.5} />
                      Resend
                    </button>
                    <button
                      type="button"
                      className="cred-dlg__btn"
                      onClick={() => handleRelink(false)}
                      disabled={busy}
                    >
                      <IconRefresh size={14} stroke={1.5} />
                      Regenerate link
                    </button>
                    <span className="cred-dlg__spacer" />
                    {confirmRevoke ? (
                      <button
                        type="button"
                        className="cred-dlg__btn cred-dlg__btn--danger"
                        onClick={handleRevoke}
                        disabled={busy}
                      >
                        <IconTrash size={14} stroke={1.5} />
                        Confirm revoke
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="cred-dlg__btn cred-dlg__btn--danger"
                        onClick={() => setConfirmRevoke(true)}
                        disabled={busy}
                      >
                        <IconTrash size={14} stroke={1.5} />
                        Revoke
                      </button>
                    )}
                  </div>
                  {confirmRevoke && (
                    <p className="cred-dlg__hint">
                      Revoking kills every link ever created for this request. Anything already
                      stored in 1Password stays there until the ticket closes.
                    </p>
                  )}
                </div>
              </div>
            </>
          )}

          {history.length > 0 && (
            <div className="cred-dlg__section">
              <div className="cred-dlg__section-hdr">
                <span className="cred-dlg__section-title">Earlier requests</span>
              </div>
              {history.map((request) => (
                <div className="cred-dlg__history-row" key={request.requestId}>
                  <span className="cred-dlg__history-label">{request.label}</span>
                  <span>{request.submissionCount} received</span>
                  <Chip tone={stateTone(request)}>{stateLabel(request)}</Chip>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <button
          type="button"
          className="cred-dlg__btn cred-dlg__btn--quiet"
          onClick={onClose}
          disabled={busy}
        >
          Close
        </button>
        {!liveRequest && (
          <button
            type="button"
            className="cred-dlg__btn cred-dlg__btn--primary"
            onClick={handleCreate}
            disabled={busy || isLoading}
          >
            <IconKey size={14} stroke={1.5} />
            {channel === 'none' ? 'Create request' : 'Create and send'}
          </button>
        )}
      </DialogActions>
    </Dialog>
  );
};
